import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { PrimeNGConfig } from 'primeng/api';
import { Storage } from '@ionic/storage-angular';
import { ActivatedRoute, NavigationEnd, Params, Router } from '@angular/router';
import { filter, map } from 'rxjs/operators';
import languages from '@app/constants/languages';
import { registerLocaleData } from '@angular/common';
import localeDe from '@angular/common/locales/de';
import localeEn from '@angular/common/locales/en';
import localeBg from '@angular/common/locales/bg';
import localeEl from '@angular/common/locales/el';
import localeEs from '@angular/common/locales/es';
import localeFi from '@angular/common/locales/fi';
import localeFr from '@angular/common/locales/fr';
import localeGa from '@angular/common/locales/ga';
import localeHu from '@angular/common/locales/hu';
import localeIt from '@angular/common/locales/it';
import localeNl from '@angular/common/locales/nl';
import localePl from '@angular/common/locales/pl';
import localePt from '@angular/common/locales/pt';
import localeRo from '@angular/common/locales/ro';
import localeSk from '@angular/common/locales/sk';
import localeSl from '@angular/common/locales/sl';
import localeSv from '@angular/common/locales/sv';


const LNG_KEY = 'SELECTED_LANGUAGE';

// Centralized locale mapping - single source of truth for language to locale conversion
const LOCALE_MAP: { [key: string]: string } = {
  'de': 'de-DE',
  'en': 'en-US',
  'bg': 'bg-BG',
  'el': 'el-GR',
  'es': 'es-ES',
  'fi': 'fi-FI',
  'fr': 'fr-FR',
  'ga': 'ga-IE',
  'hu': 'hu-HU',
  'it': 'it-IT',
  'nl': 'nl-NL',
  'pl': 'pl-PL',
  'pt': 'pt-PT',
  'ro': 'ro-RO',
  'sk': 'sk-SK',
  'sl': 'sl-SI',
  'sv': 'sv-SE'
};

@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  constructor(
    private config: PrimeNGConfig,
    private translate: TranslateService,
    private storage: Storage,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  async initializeLanguage() {
    registerLocaleData(localeDe);
    registerLocaleData(localeEn);
    registerLocaleData(localeBg);
    registerLocaleData(localeEl);
    registerLocaleData(localeEs);
    registerLocaleData(localeFi);
    registerLocaleData(localeFr);
    registerLocaleData(localeGa);
    registerLocaleData(localeHu);
    registerLocaleData(localeIt);
    registerLocaleData(localeNl);
    registerLocaleData(localePl);
    registerLocaleData(localePt);
    registerLocaleData(localeRo);
    registerLocaleData(localeSk);
    registerLocaleData(localeSl);
    registerLocaleData(localeSv);

    const defaultLanguage = 'de';
    const availableLanguages = languages;

    this.translate.addLangs(availableLanguages);
    this.translate.setDefaultLang(defaultLanguage);

    // Initialize storage
    await this.storage.create();

    // Get stored language or query param, fall back to default
    const storedLanguage = await this.storage.get(LNG_KEY);
    const queryParamLanguage = this.route.snapshot.queryParamMap.get('locale');
    const initialLanguage = queryParamLanguage || storedLanguage || defaultLanguage;

    // Set initial language immediately
    if (availableLanguages.includes(initialLanguage)) {
      await this.setLanguage(initialLanguage);
    } else {
      await this.setLanguage(defaultLanguage);
    }

    // Subscribe to route changes to update language
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(
          () =>
            this.route.snapshot.queryParamMap.get('locale') || storedLanguage || defaultLanguage
        )
      )
      .subscribe((lang: string) => {
        if (availableLanguages.includes(lang)) {
          this.setLanguage(lang);
        } else {
          this.setLanguage(defaultLanguage);
        }
      });
  }

  getSelected() {
    return this.translate.currentLang || this.translate.defaultLang;
  }

  async setLanguage(lng: string) {
    this.translate.use(lng);
    await this.storage.set(LNG_KEY, lng);
    this.translate
      .get('primeng')
      .subscribe((res) => this.config.setTranslation(res));
  }

  get datePlaceholder() {
    if(this.translate.currentLang === 'de') {
      return 'TT.MM.JJJJ';
    }else{
      return 'MM/DD/YYYY';
    }
  }

  get dateFormatLocale() {
    if(this.translate.currentLang === 'de') {
      return 'dd.MM.yyyy';
    }else{
      return 'MM/dd/yyyy';
    }
  }

  get pDateFormatLocale() {
    if(this.translate.currentLang === 'de') {
      return 'dd.mm.yy';
    }else{
      return 'mm/dd/yy';
    }
  }

  /**
   * Get the proper locale identifier for the current language
   * @param lang - Optional language code. If not provided, uses current language
   * @returns Locale identifier (e.g., 'de-DE', 'en-US')
   */
  getLocaleId(lang?: string): string {
    const currentLang = lang || this.translate.currentLang || this.translate.defaultLang || 'de';
    return LOCALE_MAP[currentLang] || 'de-DE';
  }

  /**
   * Get the locale map for all supported languages
   * @returns Object mapping language codes to locale identifiers
   */
  static getLocaleMap(): { [key: string]: string } {
    return { ...LOCALE_MAP };
  }
}
